import {
  Avatar,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Drawer,
  Link,
  List,
  ListItemButton,
  Slider,
  Stack,
  SxProps,
  Typography,
  useTheme,
} from '@mui/material'
import { useCallback, useState } from 'react'
import { Divider, IconButton, Icons } from 'utoniq-ui'

import { ArtistPostVideoFragment } from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { config } from '@/config'
import { useCurrentUser, useMessage, useRouter, useTranslation } from '@/core'
import { Share } from '@/core/native'
import { filterHiddenItem } from '@/lib/array'
import { usePrivateAction } from '@/lib/privateAction'
import { useCreateShortLink } from '@/lib/url/shortLink'

type Props = {
  post: ArtistPostVideoFragment
  state: {
    isPlaying: boolean
    duration: number
    currentTime: number
  }
  action: {
    play: () => void
    pause: () => void
    seek: (time: number) => void
    onLike: () => void
    onReportArtist?: () => Promise<void>
    onBlockArtist?: () => Promise<void>
    onClose: () => void
  }
}
export const ShortVideoControl = ({
  post,
  action,
  state: { isPlaying, duration, currentTime },
}: Props) => {
  const { zIndex } = useTheme()
  const { routerPages, generatePath, router } = useRouter()
  const { handleLoginModal } = usePrivateAction()
  const { t: tcommon } = useTranslation()
  const { showError } = useMessage()
  const { t } = useTranslation('artistPostVideos')
  const { user } = useCurrentUser()

  const getShortLinkUrl = useCreateShortLink({
    path: generatePath(routerPages.PostVideoDetailModal, {
      postId: post?.id,
    }),
  })

  const onClickShare = async () => {
    const title = post?.artist.displayName
    const url = await getShortLinkUrl()
    await Share.share({
      title: title ?? tcommon('metaTitle'),
      text: post?.content ?? tcommon('metaDescription'),
      url,
      dialogTitle: title ?? tcommon('metaTitle'),
    }).catch(e => showError(e))

    return
  }

  // NOTE アーティスト報告機能関連
  const [openDrawer, setOpenDrawer] = useState<boolean>(false)

  const toggleDrawer = useCallback(
    (open: boolean) => () => {
      setOpenDrawer(open)
    },
    []
  )
  const drawerActions = [
    {
      title: t('report'),
      async onClick() {
        if (!action.onReportArtist) {
          return
        }
        await action.onReportArtist()
        setOpenDrawer(false)
      },
      divider: false,
    },
    {
      title: t('block'),
      hidden: config.applicationType === 'standalone',
      async onClick() {
        if (!action.onBlockArtist) {
          return
        }
        await action.onBlockArtist()
        setOpenDrawer(false)
      },
      divider: true,
    },
    {
      title: t('cancel'),
      onClick: toggleDrawer(false),
      codivider: false,
      titleColor: 'primary.main',
    },
  ]

  /**
   * @description
   * いいね機能を無効化しているか否か
   * 無効化している場合はいいね関連機能を非表示にする
   */
  const disableLikes = post.artist.settings?.features?.post?.disableLikes
  /**
   * @description
   * コメント機能を無効化しているか否か
   * 無効化している場合はコメント関連機能を非表示にする
   */
  const disableReplies = post.artist.settings?.features?.post?.disableReplies

  return (
    <>
      <Card
        sx={{
          position: 'absolute',
          left: 0,
          bottom: 0,
          width: '100%',
          background:
            'linear-gradient(0deg, rgba(51, 51, 51, 0.85) 42%, rgba(0, 0, 0, 0.00) 100%)',
          boxShadow: 'none',
          zIndex: zIndex.modal + 2,
        }}
      >
        <CardContent sx={{ p: 0, pr: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {isPlaying ? (
              <IconButton.Pause
                onClick={action.pause}
                size="large"
                color="white"
              />
            ) : (
              <IconButton.PlayFilled
                onClick={action.play}
                size="large"
                color="white"
              />
            )}
            <Typography
              variant="body1"
              color="white"
              sx={{ whiteSpace: 'nowrap' }}
            >
              {secondToMmss(currentTime)} / {secondToMmss(duration)}
            </Typography>
            <Slider
              sx={{
                '& .MuiSlider-thumb': {
                  width: 0,
                  height: 0,
                  '&:focus, &:hover, &.Mui-active': {
                    boxShadow: 'none',
                  },
                },
                '& .MuiSlider-track': {
                  color: 'white',
                  height: '5px',
                },
                '& .MuiSlider-rail': {
                  color: '#4d4d4d',
                  height: '5px',
                },
                '& .MuiSlider-active': {
                  color: 'none',
                },
              }}
              value={currentTime}
              max={duration}
              onChange={(_, value) => action.seek(value as number)}
            />
          </Box>
        </CardContent>
        <CardHeader
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            py: 2,
            height: '46px',
          }}
          avatar={
            <Link
              href={generatePath(routerPages.UserProfile, {
                userId: post.artist.id,
              })}
            >
              <Avatar
                src={post.artist.avatar?.smallUri || undefined}
                sx={{
                  width: 28,
                  height: 28,
                  mr: -2,
                  position: 'relative',
                  zIndex: 2,
                  color: 'white',
                }}
              />
            </Link>
          }
          title={
            <Link
              underline="none"
              href={generatePath(routerPages.UserProfile, {
                userId: post.artist.id,
              })}
            >
              <Box
                sx={{
                  display: 'flex',
                  zIndex: 2,
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 2,
                  color: 'white',
                }}
              >
                <Typography variant="h5" color="white">
                  {post.artist.displayName}
                </Typography>
                <Typography variant="caption" color="white">
                  {post.formattedRelativePublishedAt}
                </Typography>
                {post?.category ? (
                  <Box
                    sx={{
                      py: '2px',
                      px: '4px',
                      borderRadius: '2px',
                      height: '16px',
                      fontSize: '10px',
                      color: 'white',
                      bgcolor: 'primary.dark',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {post?.category?.name}
                  </Box>
                ) : (
                  <></>
                )}
              </Box>
            </Link>
          }
          titleTypographyProps={{
            sx: { width: 'fit-content' },
          }}
          action={
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              height="100%"
              sx={{ marginTop: '5px', paddingRight: '8px' }}
            >
              {post.isPremium && (
                <Icons.MembersOnly
                  sx={{ color: 'primary.dark', margin: '6px' }}
                />
              )}
              {user?.uid !== post.artist.id && (
                <IconButton.More
                  size="small"
                  onClick={toggleDrawer(true)}
                  color="white"
                />
              )}
            </Stack>
          }
        />

        <CardActions sx={{ py: 0, height: '48px' }}>
          {!disableLikes && (
            <>
              <IconButton.Like
                color="white"
                liked={post.isLike}
                onClick={handleLoginModal(e => {
                  e.stopPropagation()
                  e.preventDefault()
                  action.onLike()
                })}
                sx={{ mx: '12px', marginLeft: 0 }}
              />
              <Typography color="white">{post.likeCount}</Typography>
            </>
          )}
          {!disableReplies && (
            <>
              <Link
                underline="none"
                href={generatePath(routerPages.PostDetail, {
                  postId: post.id,
                })}
                onClick={event => {
                  if (
                    router.pathname ===
                    generatePath(routerPages.PostDetail, {
                      postId: post.id,
                    })
                  ) {
                    event.preventDefault()
                  }
                }}
              >
                <IconButton.Reply sx={{ mx: '12px' }} color="white" />
              </Link>
              <Link
                underline="none"
                href={generatePath(routerPages.PostDetail, {
                  postId: post.id,
                })}
                onClick={event => {
                  if (
                    router.pathname ===
                    generatePath(routerPages.PostDetail, {
                      postId: post.id,
                    })
                  ) {
                    event.preventDefault()
                  }
                }}
                style={{ margin: 0, marginRight: '16px' }}
              >
                <Typography color="white">{post.replyCount}</Typography>
              </Link>
            </>
          )}
          <IconButton.Share onClick={onClickShare} color="white" />
        </CardActions>
        <CardContent sx={{ pt: 0, pb: '8px' }}>
          <Link
            underline="none"
            href={generatePath(routerPages.PostDetail, {
              postId: post.id,
            })}
            onClick={event => {
              if (
                router.pathname ===
                generatePath(routerPages.PostDetail, {
                  postId: post.id,
                })
              ) {
                event.preventDefault()
              }
            }}
          >
            <Typography
              color="white"
              textOverflow="ellipsis"
              overflow="hidden"
              variant="body1"
              sx={
                {
                  minHeight: '5vh',
                  wordBreak: 'break-all',
                  '&': { ...styleSets.lineCamp(2) },
                } as SxProps
              }
            >
              {post.content}
            </Typography>
          </Link>
        </CardContent>
      </Card>
      {/* アーティストの報告・ブロックメニュー */}
      <Drawer open={openDrawer} onClose={toggleDrawer(false)}>
        <Box sx={{ p: 5 }}>
          <Typography variant="h6" color="grey.600" align="center">
            {post.artist.displayName}
          </Typography>
        </Box>
        <Divider />
        <List>
          {filterHiddenItem(drawerActions).map(
            ({ title, onClick, divider, titleColor }, index) => (
              <ListItemButton
                divider={divider}
                key={index}
                component={Box}
                sx={{ height: 52, justifyContent: 'center' }}
                onClick={handleLoginModal(onClick)}
              >
                <Typography color={titleColor} variant="h6">
                  {title}
                </Typography>
              </ListItemButton>
            )
          )}
        </List>
      </Drawer>
    </>
  )
}

const secondToMmss = (second: number) =>
  `${Math.floor(second / 60)
    .toString()
    .padStart(1, '0')}:${Math.floor(second % 60)
    .toString()
    .padStart(2, '0')}`
